import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  Plus,
  Trash2,
  FileSpreadsheet,
  Loader2,
  Filter,
  X,
  Save,
  RotateCcw,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { mailLogService } from "../../services/mailLogService";

const TABLE_COLUMNS = [
  { key: "client", fieldKey: "client_name", label: "Client" },
  { key: "document", fieldKey: "document", label: "Document Sent" },
  { key: "cmr", fieldKey: "cmr", label: "CMR #" },
  { key: "date", fieldKey: "date", label: "Date Sent" },
  { key: "delivered", fieldKey: "delivered", label: "Delivered" },
  { key: "url", fieldKey: "url", label: "URL" },
];

const EMPTY_DRAFT = TABLE_COLUMNS.reduce((acc, { fieldKey }) => {
  acc[fieldKey] = "";
  return acc;
}, {});

const DATE_FIELD_KEYS = new Set(["date", "delivered", "usps_claim"]);

const isDateField = (fieldKey) => DATE_FIELD_KEYS.has(fieldKey);

const normalizeDateInputValue = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const isoDateMatch = text.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (isoDateMatch) return isoDateMatch[1];
  return text;
};

const normalizeDraftFieldValue = (fieldKey, value) => {
  if (!isDateField(fieldKey)) return value ?? "";
  return normalizeDateInputValue(value);
};

const normalizeCellValue = (value) => {
  if (value === null || value === undefined) return "—";
  const text = String(value).trim();
  return text.length > 0 ? text : "—";
};

const buildDraftFromRow = (row) =>
  TABLE_COLUMNS.reduce((acc, { fieldKey }) => {
    acc[fieldKey] = normalizeDraftFieldValue(
      fieldKey,
      row?.mail_log?.[fieldKey],
    );
    return acc;
  }, {});

const serializeDraft = (draft = {}) =>
  TABLE_COLUMNS.reduce((acc, { fieldKey }) => {
    acc[fieldKey] = normalizeDraftFieldValue(fieldKey, draft[fieldKey]);
    return acc;
  }, {});

const hasAnyValue = (draft = {}) =>
  TABLE_COLUMNS.some(
    ({ fieldKey }) => String(draft[fieldKey] ?? "").trim() !== "",
  );

const areDraftsEqual = (left = {}, right = {}) =>
  TABLE_COLUMNS.every(
    ({ fieldKey }) =>
      String(left[fieldKey] ?? "") === String(right[fieldKey] ?? ""),
  );

const FILTERABLE_COLUMNS = TABLE_COLUMNS.map(({ key, label, fieldKey }) => ({
  key,
  label,
  accessor: (log) => normalizeCellValue(log?.mail_log?.[fieldKey]),
}));

const FILTERABLE_COLUMN_MAP = FILTERABLE_COLUMNS.reduce((acc, column) => {
  acc[column.key] = column;
  return acc;
}, {});

const ColumnFilter = ({
  columnKey,
  label,
  options = [],
  selectedValues,
  onApply,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [draftSelection, setDraftSelection] = useState(new Set());
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const selectAllRef = useRef(null);
  const isActive = Array.isArray(selectedValues);
  const alignLeft = columnKey === "client";

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: alignLeft ? rect.left : rect.right - 256,
      });
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const baseSelection =
      selectedValues && selectedValues.length > 0 ? selectedValues : options;
    setDraftSelection(new Set(baseSelection));
    setSearchTerm("");
  }, [isOpen, options, selectedValues]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      options.length > 0 &&
      draftSelection.size > 0 &&
      draftSelection.size < options.length;
  }, [draftSelection, options]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const matcher = searchTerm.trim().toLowerCase();
    return options.filter((option) => option.toLowerCase().includes(matcher));
  }, [options, searchTerm]);

  const toggleOption = (value) => {
    setDraftSelection((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (draftSelection.size === options.length) {
      setDraftSelection(new Set());
      return;
    }
    setDraftSelection(new Set(options));
  };

  const handleApply = () => {
    if (options.length === 0 || draftSelection.size === options.length) {
      onApply(columnKey, null);
    } else {
      onApply(columnKey, Array.from(draftSelection));
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    onApply(columnKey, null);
    setDraftSelection(new Set(options));
    setIsOpen(false);
  };

  const allSelected =
    options.length > 0 && draftSelection.size === options.length;

  return (
    <div className="inline-flex items-center">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={`Filter ${label}`}
        className={`p-1 rounded border border-transparent hover:border-canopy-border transition-colors ${
          isActive ? "text-canopy-blue" : "text-canopy-textMuted"
        }`}
        onClick={handleToggle}
      >
        <Filter className="h-4 w-4" />
        <span className="sr-only">Filter {label}</span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: dropdownPos.top,
              left: dropdownPos.left,
              zIndex: 9999,
              width: 256,
            }}
            className="bg-white border border-canopy-border rounded-lg shadow-xl p-3"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-canopy-text uppercase tracking-wide">
                {label}
              </span>
              <button
                type="button"
                className="text-xs text-canopy-textMuted hover:text-canopy-text"
                onClick={() => setIsOpen(false)}
              >
                Close
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={options.length > 0 && allSelected}
                onChange={toggleAll}
              />
              Select all
            </label>

            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search values..."
              className="w-full border border-canopy-border rounded px-2 py-1 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-canopy-blue"
            />

            <div className="max-h-48 overflow-auto border border-canopy-border rounded">
              {filteredOptions.length === 0 ? (
                <div className="px-2 py-3 text-sm text-canopy-textMuted text-center">
                  No matches
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <label
                    key={`${columnKey}-${option}`}
                    className="flex items-center gap-2 px-2 py-1 text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={draftSelection.has(option)}
                      onChange={() => toggleOption(option)}
                    />
                    <span className="truncate" title={option}>
                      {option}
                    </span>
                  </label>
                ))
              )}
            </div>

            <div className="flex items-center justify-between pt-3 text-xs">
              <button
                type="button"
                className="text-canopy-textMuted hover:text-canopy-text"
                onClick={handleClear}
              >
                Clear
              </button>
              <button
                type="button"
                className="text-canopy-blue hover:text-canopy-blueHover font-semibold"
                onClick={handleApply}
              >
                Apply
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const MailLog = ({
  userPermission,
  mailLog = [],
  loading,
  onMailLogCreated,
  onMailLogUpdated,
  onMailLogDeleted,
  searchTerm,
  totalMailLog = 0,
  currentPage = 1,
  rowsPerPage = 50,
  onPageChange,
  onRowsPerPageChange,
  onFiltersChange,
}) => {
  const canEdit =
    userPermission === "fullAccess" || userPermission === "masterAccess";
  const canDelete = userPermission === "masterAccess";
  const canExport = userPermission === "masterAccess";
  const [exporting, setExporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [draftRows, setDraftRows] = useState({});
  const [newRowDraft, setNewRowDraft] = useState({ ...EMPTY_DRAFT });
  const [savingRowIds, setSavingRowIds] = useState({});
  const [deletingRowIds, setDeletingRowIds] = useState({});

  useEffect(() => {
    setDraftRows((prev) => {
      const next = { ...prev };
      const serverIds = new Set();

      mailLog.forEach((row) => {
        const rowId = String(row.id);
        serverIds.add(rowId);
        const serverDraft = buildDraftFromRow(row);
        const existingDraft = next[row.id];

        if (!existingDraft || areDraftsEqual(existingDraft, serverDraft)) {
          next[row.id] = serverDraft;
        }
      });

      Object.keys(next).forEach((rowId) => {
        if (!serverIds.has(String(rowId))) {
          delete next[rowId];
        }
      });

      return next;
    });
  }, [mailLog]);

  const [columnValueOptions, setColumnValueOptions] = useState({});

  const loadOptions = useCallback(async () => {
    try {
      const opts = await mailLogService.getMailLogOptions();
      setColumnValueOptions(opts);
    } catch (e) {
      console.error("Failed to load mail log options", e);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const handleFilterApply = (columnKey, values) => {
    const next = { ...columnFilters };
    if (!Array.isArray(values) || values.length === 0) {
      delete next[columnKey];
    } else {
      next[columnKey] = values;
    }
    setColumnFilters(next);
    onFiltersChange?.(next, { resetPage: true });
  };

  const handleClearAllFilters = () => {
    setColumnFilters({});
    onFiltersChange?.({}, { resetPage: true });
  };

  const activeFilterSummaries = Object.entries(columnFilters)
    .filter(([, values]) => Array.isArray(values))
    .map(([key, values]) => ({
      key,
      label: FILTERABLE_COLUMN_MAP[key]?.label || key,
      values,
    }));

  const hasActiveFilters = activeFilterSummaries.length > 0;
  const showFilteredEmptyState = hasActiveFilters && mailLog.length === 0;
  const safeRows = rowsPerPage || 1;
  const totalPages =
    totalMailLog === 0 ? 1 : Math.max(1, Math.ceil(totalMailLog / safeRows));
  const rawPageStart =
    totalMailLog === 0 ? 0 : (currentPage - 1) * safeRows + 1;
  const displayPageStart =
    totalMailLog === 0 ? 0 : Math.min(rawPageStart, totalMailLog);
  const displayPageEnd =
    totalMailLog === 0
      ? 0
      : mailLog.length > 0
        ? Math.min(displayPageStart + mailLog.length - 1, totalMailLog)
        : displayPageStart;

  const getVisiblePages = () => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, currentPage - half);
    let end = start + maxButtons - 1;
    if (end > totalPages) {
      end = totalPages;
      start = totalPages - maxButtons + 1;
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleDraftChange = (rowId, fieldKey, value) => {
    setDraftRows((prev) => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || { ...EMPTY_DRAFT }),
        [fieldKey]: value,
      },
    }));
  };

  const handleNewDraftChange = (fieldKey, value) => {
    setNewRowDraft((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const isRowSaving = (rowId) => Boolean(savingRowIds[rowId]);
  const isRowDeleting = (rowId) => Boolean(deletingRowIds[rowId]);

  const isRowDirty = (row) => {
    const currentDraft = draftRows[row.id] || { ...EMPTY_DRAFT };
    const serverDraft = buildDraftFromRow(row);
    return !areDraftsEqual(currentDraft, serverDraft);
  };

  const handleResetRow = (row) => {
    setDraftRows((prev) => ({
      ...prev,
      [row.id]: buildDraftFromRow(row),
    }));
  };

  const handleSaveRow = async (row) => {
    if (!canEdit) {
      toast.error("You do not have permission to update mail log entries");
      return;
    }

    const rowId = row.id;
    const payload = serializeDraft(draftRows[rowId] || buildDraftFromRow(row));

    if (!isRowDirty(row)) {
      toast("No changes to save");
      return;
    }

    try {
      setSavingRowIds((prev) => ({ ...prev, [rowId]: true }));
      await mailLogService.updateMailLog(rowId, payload);
      toast.success("Mail log updated successfully");
      onMailLogUpdated?.(rowId);
    } catch (error) {
      toast.error("Failed to update mail log");
      console.error("Mail log update error:", error);
    } finally {
      setSavingRowIds((prev) => {
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
    }
  };

  const handleCreateRow = async () => {
    if (!canEdit) {
      toast.error("You do not have permission to create mail log entries");
      return;
    }

    const payload = serializeDraft(newRowDraft);
    if (!hasAnyValue(payload)) {
      toast.error("Fill at least one field before creating a row");
      return;
    }

    try {
      setCreating(true);
      const createdMailLog = await mailLogService.createMailLog(payload);
      setNewRowDraft({ ...EMPTY_DRAFT });
      onMailLogCreated?.(createdMailLog);
      loadOptions();
      toast.success("Mail log created successfully");
    } catch (error) {
      toast.error("Failed to create mail log");
      console.error("Mail log create error:", error);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteMailLog = async (mailLogId, clientName) => {
    if (!canDelete) {
      toast.error("You do not have permission to delete mail log entries");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${clientName} mail log? This action cannot be undone.`,
      ) &&
      window.confirm(
        `Do you really want to delete ${clientName} mail log? Click “Ok” only if you are absolutely sure.`,
      )
    ) {
      try {
        setDeletingRowIds((prev) => ({ ...prev, [mailLogId]: true }));
        await mailLogService.deleteMailLog(mailLogId);
        onMailLogDeleted?.(mailLogId);
        loadOptions();
        toast.success("Mail log deleted successfully");
      } catch (error) {
        toast.error("Failed to delete mail log");
        console.error("Mail log delete error:", error);
      } finally {
        setDeletingRowIds((prev) => {
          const next = { ...prev };
          delete next[mailLogId];
          return next;
        });
      }
    }
  };

  const handleExport = async () => {
    try {
      if (!canExport) {
        toast.error(
          "You do not have permission to export mail log information",
        );
        return;
      }
      setExporting(true);
      const blob = await mailLogService.exportMailLog({
        search: (searchTerm || "").trim() || undefined,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mail_log_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="canopy-spinner mx-auto mb-4"></div>
          <p className="text-canopy-textMuted">Loading mail log...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm animate-fadeIn">
      <div className="canopy-tabs flex items-center">
        <div className="ml-auto mt-2 mb-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-3 py-1 text-xs inline-flex items-center disabled:opacity-60"
            title="Export all mail log (filters applied)"
            aria-label="Export mail log to Excel"
          >
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export to Excel
              </>
            )}
          </button>
        </div>
      </div>

      {hasActiveFilters && (
        <div className="px-6 py-3 border-b border-canopy-border bg-gray-50">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-canopy-textMuted">
              Active Filters
            </span>
            {activeFilterSummaries.map(({ key, label, values }) => {
              const preview =
                values.length === 0
                  ? "No values"
                  : values.length <= 2
                    ? values.join(", ")
                    : `${values.length} selected`;
              return (
                <span
                  key={key}
                  className="inline-flex items-center gap-2 bg-white border border-canopy-border rounded-full px-3 py-1 text-xs text-canopy-text"
                >
                  <span className="font-medium">{label}:</span>
                  <span
                    className="truncate max-w-[8.5rem]"
                    title={
                      values.length === 0
                        ? "No values selected"
                        : values.join(", ")
                    }
                  >
                    {preview}
                  </span>
                  <button
                    type="button"
                    className="text-canopy-textMuted hover:text-canopy-text"
                    onClick={() => handleFilterApply(key, null)}
                    aria-label={`Clear ${label} filter`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              );
            })}
            <button
              type="button"
              className="ml-auto text-xs text-canopy-blue hover:text-canopy-blueHover font-medium"
              onClick={handleClearAllFilters}
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="canopy-table min-w-[1900px]">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((column) => (
                <th key={column.key} className="min-w-[160px]">
                  <div className="flex items-center gap-2">
                    <span>{column.label}</span>
                    <ColumnFilter
                      columnKey={column.key}
                      label={column.label}
                      options={columnValueOptions[column.key] || []}
                      selectedValues={columnFilters[column.key]}
                      onApply={handleFilterApply}
                    />
                  </div>
                </th>
              ))}
              <th className="w-44">Actions</th>
            </tr>
          </thead>
          <tbody>
            {canEdit && (
              <tr className="bg-blue-50/30">
                {TABLE_COLUMNS.map((column) => (
                  <td key={`new-${column.key}`}>
                    <input
                      type={isDateField(column.fieldKey) ? "date" : "text"}
                      value={newRowDraft[column.fieldKey] ?? ""}
                      onChange={(e) =>
                        handleNewDraftChange(column.fieldKey, e.target.value)
                      }
                      className="w-full min-w-[9rem] border border-canopy-border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-canopy-blue"
                      placeholder={column.label}
                    />
                  </td>
                ))}
                <td>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="p-1 text-canopy-textMuted hover:text-canopy-blue rounded transition-colors disabled:opacity-40"
                      title="Create mail log row"
                      onClick={handleCreateRow}
                      disabled={creating}
                    >
                      {creating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      type="button"
                      className="p-1 text-canopy-textMuted hover:text-canopy-text rounded transition-colors disabled:opacity-40"
                      title="Clear new row"
                      onClick={() => setNewRowDraft({ ...EMPTY_DRAFT })}
                      disabled={creating || !hasAnyValue(newRowDraft)}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {mailLog.length === 0 ? (
              <tr>
                <td
                  colSpan={TABLE_COLUMNS.length + 1}
                  className="text-center py-12"
                >
                  {showFilteredEmptyState ? (
                    <div className="text-canopy-textMuted">
                      <Filter className="mx-auto h-12 w-12 mb-4" />
                      <h3 className="text-lg font-medium text-canopy-text mb-2">
                        No mail log match the selected filters
                      </h3>
                      <p className="mb-6">
                        Try adjusting or clearing your column filters to see
                        more results.
                      </p>
                      <button
                        type="button"
                        onClick={handleClearAllFilters}
                        className="canopy-btn-secondary inline-flex items-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        Clear filters
                      </button>
                    </div>
                  ) : (
                    <div className="text-canopy-textMuted">
                      <Plus className="mx-auto h-12 w-12 mb-4" />
                      <h3 className="text-lg font-medium text-canopy-text mb-2">
                        No mail log found
                      </h3>
                      {canEdit ? (
                        <p>
                          Use the editable row above to create the first entry.
                        </p>
                      ) : (
                        <p>Adjust your search criteria and try again.</p>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              mailLog.map((row) => {
                const rowDraft = draftRows[row.id] || buildDraftFromRow(row);
                const dirty = isRowDirty(row);
                const saving = isRowSaving(row.id);
                const deleting = isRowDeleting(row.id);
                const rowBusy = saving || deleting;
                const clientName = normalizeCellValue(
                  row?.mail_log?.client_name,
                );

                return (
                  <tr key={row.id}>
                    {TABLE_COLUMNS.map((column) => (
                      <td
                        key={`${row.id}-${column.key}`}
                        className="min-w-[160px]"
                      >
                        {canEdit ? (
                          <input
                            type={
                              isDateField(column.fieldKey) ? "date" : "text"
                            }
                            value={rowDraft[column.fieldKey] ?? ""}
                            onChange={(e) =>
                              handleDraftChange(
                                row.id,
                                column.fieldKey,
                                e.target.value,
                              )
                            }
                            disabled={rowBusy}
                            className={`w-full min-w-[9rem] border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-canopy-blue disabled:bg-gray-100 disabled:text-canopy-textMuted ${
                              column.fieldKey === "delivered" &&
                              !rowDraft[column.fieldKey]
                                ? "border-red-400 text-red-500"
                                : "border-canopy-border"
                            }`}
                          />
                        ) : (
                          <span
                            className={
                              column.fieldKey === "delivered" &&
                              !String(
                                row?.mail_log?.[column.fieldKey] ?? "",
                              ).trim()
                                ? "text-red-500"
                                : "text-canopy-textMuted"
                            }
                          >
                            {normalizeCellValue(
                              row?.mail_log?.[column.fieldKey],
                            )}
                          </span>
                        )}
                      </td>
                    ))}
                    <td>
                      {canEdit && (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleSaveRow(row)}
                            disabled={!dirty || rowBusy}
                            className="p-1 text-canopy-textMuted hover:text-canopy-blue rounded transition-colors disabled:opacity-40"
                            title="Save row"
                          >
                            {saving ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleResetRow(row)}
                            disabled={!dirty || rowBusy}
                            className="p-1 text-canopy-textMuted hover:text-canopy-text rounded transition-colors disabled:opacity-40"
                            title="Reset row"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteMailLog(row.id, clientName)
                              }
                              disabled={rowBusy}
                              className="p-1 text-canopy-textMuted hover:text-red-600 rounded transition-colors disabled:opacity-40"
                              title="Delete Mail Log"
                            >
                              {deleting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalMailLog > 0 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-canopy-border">
          <div className="flex items-center space-x-2 text-sm text-canopy-textMuted">
            <span>
              Showing {displayPageStart}-{displayPageEnd} of {totalMailLog}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm text-canopy-textMuted hover:text-canopy-text disabled:opacity-40"
              aria-label="Previous page"
            >
              ←
            </button>
            {getVisiblePages().map((page) => (
              <button
                key={page}
                onClick={() => onPageChange?.(page)}
                className={`px-3 py-1 text-sm rounded ${
                  page === currentPage
                    ? "bg-canopy-blue text-white"
                    : "text-canopy-textMuted hover:text-canopy-text"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() =>
                onPageChange?.(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm text-canopy-textMuted hover:text-canopy-text disabled:opacity-40"
              aria-label="Next page"
            >
              →
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <select
              className="text-sm border border-canopy-border rounded px-2 py-1"
              value={rowsPerPage}
              onChange={(e) => {
                onRowsPerPageChange?.(Number(e.target.value));
              }}
            >
              {[50, 100, 200].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-canopy-textMuted">per page</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailLog;
